<template>
  <div>
    <div class="flex justify-between items-start gap-lg">
      <h1>Party Card</h1>

      <BaseButton
        class="!p-0 ml-auto"
        icon="arrow-down"
        variant="invisible"
        @click.native="showParties = !showParties"
      >
        show json
      </BaseButton>
    </div>

    <div
      v-for="(example, index) in examples"
      :key="index"
      class="p-sm sm:p-lg mb-xxl bg-whiteOverride rounded-sm"
    >
      <h2>{{ example.title }}</h2>

      <PartyCard
        :party="example.party"
        :partyIndex="index"
        :canRemove="true"
        :errors="example.errors"
        :validate="() => {}"
        @toggleCopyOnly="example.toggleCopyOnly"
        :roles="example.roles"
        @toggleRoleSelected="example.toggleRoleSelected"
        @addMyselfAsParty="example.addMyselfAsParty"
      />

      <div v-if="showParties">
        <hr />
        <pre :key="index"
          >{{ JSON.stringify(example.party, null, 2) }}
        </pre>
      </div>
    </div>
  </div>
</template>

<script setup>
// @ts-check
import { ref } from 'vue';

import {
  createDocumentRoles,
  createDocuments,
} from '@/data/factories/documents';
import { useParties } from '@/composables/parties/parties';
import { useAssignRoles, useRoles } from '@/composables/roles/roles';
import { createParty } from '@/store/modules/envelopes/factories/parties';

import BaseButton from '@/components/library/buttons/BaseButton.vue';
import PartyCard from '@/components/ui/envelopeCreate/PartyCard/PartyCard.vue';

/** @typedef {import('@/data/schema/parties').TNewOrSavedParty} TNewOrSavedParty */
/** @typedef {import('@/data/schema/parties').TCreatingParties} TCreatingParties */
/** @typedef {import('@/data/schema/parties').TCreatingParty} TCreatingParty */
/** @typedef {import('@/data/schema/documents').TDocuments} TDocuments */

/**
 * @param {string} title
 * @param {TCreatingParty} party
 * @param {TDocuments} documents
 * @param {import('zod').ZodFormattedError<any>} [errors]
 */
function createExample(title, party, documents, errors) {
  const partiesRef = ref([party]);
  const documentsRef = ref(documents);

  const { parties } = useParties(partiesRef, documentsRef);
  const roles = useRoles(parties, documentsRef);
  const { toggleCopyOnly, toggleRoleSelected } = useAssignRoles(
    parties,
    documentsRef,
  );

  function addMyselfAsParty() {
    // eslint-disable-next-line no-param-reassign
    party.title = 'My Self';
    // eslint-disable-next-line no-param-reassign
    party.email = 'me@email.me';
  }

  return {
    title,
    party: parties.value[0],
    documents,
    errors,
    numRoles: documents
      .map(document => document.parties.length)
      .reduce((a, b) => a + b, 0),
    toggleCopyOnly,
    roles,
    toggleRoleSelected,
    addMyselfAsParty,
  };
}

const roles = createDocumentRoles(2);
const examples = [
  createExample(
    'Good example',
    createParty({ role_ids: [roles[1].id] }),
    createDocuments(1, [{ parties: [roles[0], roles[1]] }]),
  ),
  createExample(
    'Bad example',
    createParty({ role_ids: [], password: ' ' }),
    createDocuments(1, [{ parties: [roles[0], roles[1]] }]),
    {
      // @ts-ignore badly faked errors
      title: { _errors: ['Bad name'] },
      message: { _errors: ['Bad message'] },
      password: { _errors: ['PasswordRequired'] },
    },
  ),
];

const showParties = ref(false);
</script>
